/**
 * How tall the docked commit bar ACTUALLY is, published for the page to reserve.
 *
 * WHY MEASURING BEAT STATING IT. `--commit-h` is a stated geometry — a 48px control, `--s-3`
 * above and below, a hairline — and the page reserves exactly that so its last card clears the
 * bar. That held for one day. Pass 06 §6 replaced the bar's left-hand sentence with a longer one
 * that wraps to two lines, the bar went from 73px to 106px, and the reservation was 33px short
 * again — the same bug, from the other direction, introduced by a copy change nobody would think
 * to check the layout for.
 *
 * A number that has to be kept in step with a string is a number that will drift. So the bar
 * measures itself and the page reads the measurement; `--commit-h` stays as the floor the bar is
 * held to, and as the fallback for the first paint and for anything rendering without a DOM.
 *
 * ResizeObserver rather than a one-shot read: the bar changes height when the text changes state
 * (submitted, closed, loading), when the viewport narrows, and when a font finally loads.
 */

import { useEffect, type RefObject } from 'react';

/** The custom property the stylesheet reads. Kept here so the two cannot disagree. */
export const DOCKED_VAR = '--commit-actual';

export function useDockedHeight(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) return undefined;

    const publish = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      /*
       * Zero means the bar is display:none — at 1100px the draft bar rejoins the flow and the
       * keeper bar is hidden outright. Clearing the property rather than writing `0px` lets the
       * stylesheet's own `var(..., fallback)` and its media queries take over, which is where
       * that case is already handled correctly.
       */
      if (h > 0) root.style.setProperty(DOCKED_VAR, `${h}px`);
      else root.style.removeProperty(DOCKED_VAR);
    };

    publish();
    /* Older Safari has no ResizeObserver on some versions people still run; fall back to resize. */
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publish);
      return () => {
        window.removeEventListener('resize', publish);
        root.style.removeProperty(DOCKED_VAR);
      };
    }
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      /*
       * CLEARED ON UNMOUNT. The variable lives on the root, so a stale value would otherwise
       * follow the reader to a screen with no docked bar at all and leave a hole at the bottom
       * of it.
       */
      root.style.removeProperty(DOCKED_VAR);
    };
  }, [ref]);
}
